/* eslint-disable */
import React, { useState, useRef, useEffect, useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import { 
  IconButton, 
  Tooltip, 
  CircularProgress, 
  Typography, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  LinearProgress,
  Box
} from '@material-ui/core'
import { 
  Mic, 
  MicOff, 
  Stop, 
  PlayArrow, 
  Pause,
  Send,
  Close,
  AddCircle
} from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { toast } from 'react-toastify'
import { client } from '../../services/axiosClient'
import AuthContext from '../../contexts/AuthProvider'

import { ThreeDots } from 'react-loader-spinner'

const useStyles = makeStyles((theme) => ({
  recordingDialog: {
    '& .MuiDialog-paper': {
      minWidth: 600,
      maxWidth: 800,
      borderRadius: 8,
      overflow: 'hidden'
    }
  },
  dialogTitle: {
    backgroundColor: '#f5f5f5',
    borderBottom: '2px solid',
    paddingBottom: '12px',
    position: 'relative',
    paddingRight: '60px' // Espaço para botão close
  },
  recordingContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(3, 4),
    gap: theme.spacing(3),
    minHeight: 120
  },
  controlSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 120
  },
  recordingButton: {
    width: 56,
    height: 56,
    color: '#424242',
    border: '2px solid #e0e0e0',
    backgroundColor: 'white',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#f5f5f5',
      borderColor: '#bdbdbd'
    },
    '&.recording': {
      backgroundColor: '#f44336',
      borderColor: '#f44336',
      color: 'white',
      animation: '$pulse 2s infinite'
    },
    '&.has-recording': {
      backgroundColor: '#4caf50',
      borderColor: '#4caf50',
      color: 'white'
    }
  },
  iconButton: {
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'scale(1.1)'
    }
  },
  cardButton: {
    minWidth: 'auto',
    padding: '8px 12px',
    borderRadius: 6,
    textTransform: 'none',
    fontSize: '0.875rem',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center'
  },
  cardButtonIcon: {
    fontSize: '18px'
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)'
    },
    '70%': {
      transform: 'scale(1.05)',
      boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)'
    },
    '100%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)'
    }
  },
  timer: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#333',
    fontFamily: 'monospace'
  },
  audioControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  progressContainer: {
    flex: 1,
    textAlign: 'center',
    padding: theme.spacing(0, 2)
  },
  loadingSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    flex: 1,
    padding: theme.spacing(2)
  },
  processingMessage: {
    transition: 'all 0.5s ease-in-out',
    animation: '$fadeIn 0.5s ease-in-out'
  },
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)'
    }
  },
  statusSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 200
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap'
  }
}))

const OnePage_SpeechRecorder = ({ 
  isOpen, 
  onClose, 
  onSpeechProcessed, 
  selectedInitiative,
  t 
}) => {
  const classes = useStyles()
  const { userDetails } = useContext(AuthContext)
  
  // Estados do componente
  const [isRecording, setIsRecording] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const [hasRecording, setHasRecording] = useState(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  const [recordingTime, setRecordingTime] = useState(0)
  const [playbackTime, setPlaybackTime] = useState(0)
  const [recordingDuration, setRecordingDuration] = useState(0)
  
  // Estados para mensagens específicas de speech-to-text
  const [processingStep, setProcessingStep] = useState('')
  const [processingStepIndex, setProcessingStepIndex] = useState(0)
  
  // Mensagens específicas para speech processing (usando useMemo para evitar recriação)
  const speechProcessingMessages = useMemo(() => [
    t('speech.processing_steps.transcribing'),
    t('speech.processing_steps.analyzing'),
    t('speech.processing_steps.writing')
  ], [t])
  
  // Refs para MediaRecorder e áudio
  const mediaRecorderRef = useRef(null)
  const audioChunksRef = useRef([])
  const audioUrlRef = useRef(null)
  const audioElementRef = useRef(null)
  const timerRef = useRef(null)
  const streamRef = useRef(null)
  const recordingTimeRef = useRef(0) // Para capturar o tempo exato da gravação
  
  // Limpeza ao fechar
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop())
      }
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current)
      }
    }
  }, [])
  
  // Timer para gravação/reprodução
  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          const newTime = prev + 1
          recordingTimeRef.current = newTime // Sempre manter o ref atualizado
          return newTime
        })
      }, 1000)
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [isRecording, isPaused])
  
  // Timer para progresso linear das mensagens de processamento
  useEffect(() => {
    let messageTimer = null
    
    if (isProcessing && speechProcessingMessages.length > 0) {
      // Inicializar com primeira mensagem
      setProcessingStep(speechProcessingMessages[0])
      setProcessingStepIndex(0)
      
      console.log('🎤 Iniciando progresso de mensagens de processamento')
      
      // Progredir mensagens a cada 3 segundos até a última
      messageTimer = setInterval(() => {
        setProcessingStepIndex(prev => {
          const nextIndex = prev + 1
          
          // Se chegou na última mensagem, para por aqui
          if (nextIndex >= speechProcessingMessages.length) {
            console.log('📍 Chegou na última etapa, parando progresso')
            if (messageTimer) {
              clearInterval(messageTimer) // Limpar o timer quando chegou na última
            }
            return prev // Mantém no último índice
          }
          
          console.log(`🔄 Progredindo para: ${speechProcessingMessages[nextIndex]}`)
          setProcessingStep(speechProcessingMessages[nextIndex])
          return nextIndex
        })
      }, 3000)
    } else {
      console.log('🛑 Finalizando processamento')
      setProcessingStep('')
      setProcessingStepIndex(0)
    }
    
    return () => {
      if (messageTimer) {
        clearInterval(messageTimer)
      }
    }
  }, [isProcessing, speechProcessingMessages])
  
  // Iniciar gravação
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100
        } 
      })
      
      streamRef.current = stream
      audioChunksRef.current = []
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      })
      
      mediaRecorderRef.current = mediaRecorder
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
        const audioUrl = URL.createObjectURL(audioBlob)
        audioUrlRef.current = audioUrl
        
        // Capturar o tempo de gravação atual do ref (mais confiável)
        const currentRecordingTime = recordingTimeRef.current
        console.log(`🎵 Gravação finalizada. Tempo gravado: ${currentRecordingTime}s`)
        
        // Criar elemento de áudio para obter duração
        const audio = new Audio(audioUrl)
        audio.addEventListener('loadedmetadata', () => {
          // Verificar se a duração é válida
          const duration = audio.duration
          console.log(`📏 Duração do áudio (metadata): ${duration}s`)
          
          if (duration && isFinite(duration) && !isNaN(duration) && duration > 0) {
            setRecordingDuration(duration)
            console.log(`✅ Usando duração do metadata: ${duration}s`)
          } else {
            // Fallback: usar o tempo de gravação
            setRecordingDuration(currentRecordingTime)
            console.log(`🔄 Fallback para recordingTime: ${currentRecordingTime}s`)
          }
        })
        
        // Fallback caso loadedmetadata não funcione
        audio.addEventListener('canplay', () => {
          setTimeout(() => {
            const currentDuration = recordingDuration
            if ((!currentDuration || !isFinite(currentDuration) || currentDuration === 0) && currentRecordingTime > 0) {
              setRecordingDuration(currentRecordingTime)
              console.log(`🔄 Fallback canplay para recordingTime: ${currentRecordingTime}s`)
            }
          }, 100)
        })
        
        // Timeout fallback definitivo
        setTimeout(() => {
          if (currentRecordingTime > 0) {
            setRecordingDuration(prev => {
              if (!prev || !isFinite(prev) || prev === 0) {
                console.log(`🔄 Fallback timeout para recordingTime: ${currentRecordingTime}s`)
                return currentRecordingTime
              }
              return prev
            })
          }
        }, 1000)
        
        setHasRecording(true)
        stream.getTracks().forEach(track => track.stop())
      }
      
      mediaRecorder.start()
      setIsRecording(true)
      setRecordingTime(0)
      recordingTimeRef.current = 0 // Resetar o ref também
      
    } catch (error) {
      console.error('Erro ao acessar microfone:', error)
      toast.error(t('speech.microphone_error'))
    }
  }
  
  // Pausar/resumir gravação
  const togglePauseRecording = () => {
    if (mediaRecorderRef.current) {
      if (isPaused) {
        mediaRecorderRef.current.resume()
        setIsPaused(false)
      } else {
        mediaRecorderRef.current.pause()
        setIsPaused(true)
      }
    }
  }
  
  // Parar gravação
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      console.log(`⏹️ Parando gravação. Tempo state: ${recordingTime}s, Tempo ref: ${recordingTimeRef.current}s`)
      mediaRecorderRef.current.stop()
      setIsRecording(false)
      setIsPaused(false)
      
      // Garantir que o tempo de gravação seja preservado
      console.log(`💾 Tempo de gravação preservado no ref: ${recordingTimeRef.current}s`)
    }
  }
  
  // Reproduzir áudio gravado
  const togglePlayback = () => {
    if (!audioUrlRef.current) return
    
    if (!audioElementRef.current) {
      audioElementRef.current = new Audio(audioUrlRef.current)
      
      audioElementRef.current.addEventListener('timeupdate', () => {
        setPlaybackTime(audioElementRef.current.currentTime)
      })
      
      audioElementRef.current.addEventListener('ended', () => {
        setIsPlaying(false)
        setPlaybackTime(0)
      })
    }
    
    if (isPlaying) {
      audioElementRef.current.pause()
      setIsPlaying(false)
    } else {
      audioElementRef.current.play()
      setIsPlaying(true)
    }
  }
  
  // Processar áudio com IA
  const processAudio = async () => {
    if (!audioChunksRef.current.length || !selectedInitiative?.id) {
      toast.error(t('speech.no_audio_or_initiative'))
      return
    }
    
    setIsProcessing(true)
    
    try {
      // Criar FormData para envio
      const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
      const formData = new FormData()
      formData.append('audio_file', audioBlob, 'recording.webm')
      formData.append('initiative_id', selectedInitiative.id)
      
      // Enviar para o backend
      const response = await client.post('/api/speech-to-onepage/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      
      if (response.data.success) {
        toast.success(t('speech.processing_success'))
        
        // Passar dados processados para o componente pai
        onSpeechProcessed({
          speechText: response.data.data.speech_text,
          organizedContent: response.data.data.organized_content
        })
        
        // Fechar modal
        handleClose()
      } else {
        throw new Error(response.data.error || t('speech.processing_error'))
      }
      
    } catch (error) {
      console.error('Erro ao processar áudio:', error)
      toast.error(error.response?.data?.error || t('speech.processing_error'))
    } finally {
      setIsProcessing(false)
    }
  }
  
  // Fechar modal e limpar estado
  const handleClose = () => {
    // Parar qualquer gravação em andamento
    if (isRecording) {
      stopRecording()
    }
    
    // Parar reprodução
    if (audioElementRef.current) {
      audioElementRef.current.pause()
      audioElementRef.current = null
    }
    
    // Limpar estados (mas preservar recordingDuration se houver gravação)
    setIsRecording(false)
    setIsPaused(false)
    setIsPlaying(false)
    setIsProcessing(false)
    setRecordingTime(0)
    setPlaybackTime(0)
    
    // Só resetar completamente se não houver gravação válida
    if (!hasRecording) {
      setRecordingDuration(0)
      recordingTimeRef.current = 0 // Só resetar o ref se não há gravação
    }
    
    // Limpar refs
    audioChunksRef.current = []
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current)
      audioUrlRef.current = null
    }
    
    // Parar stream
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop())
      streamRef.current = null
    }
    
    onClose()
  }
  
  // Formatar tempo
  const formatTime = (seconds) => {
    // Verificar se é um número válido
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) {
      return '00:00'
    }
    
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  
  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      className={classes.recordingDialog}
      disableBackdropClick={isRecording || isProcessing}
      disableEscapeKeyDown={isRecording || isProcessing}
    >
      <DialogTitle 
        className={classes.dialogTitle}
        style={{ 
          borderBottomColor: userDetails?.implementation_color || '#1976d2'
        }}
      >
        <Typography variant="h6" style={{ fontSize: '1.1rem', fontWeight: 500 }}>
          {t('speech.record_audio')}
        </Typography>
        <Typography 
          variant="caption" 
          style={{ 
            color: '#666', 
            fontStyle: 'italic',
            fontSize: '0.75rem',
            display: 'block',
            marginTop: '4px'
          }}
        >
          {t('speech.dialog_subtitle')}
        </Typography>
        
        {/* Botão close no header */}
        <div style={{ position: 'absolute', right: 8, top: 8 }}>
          <Tooltip title="Fechar">
            <IconButton
              onClick={handleClose}
              disabled={isRecording || isProcessing}
              style={{
                color: '#424242',
                padding: '8px',
                cursor: isRecording || isProcessing ? 'not-allowed' : 'pointer',
                opacity: isRecording || isProcessing ? 0.5 : 1
              }}
            >
              <Close style={{ fontSize: '20px' }} />
            </IconButton>
          </Tooltip>
        </div>
      </DialogTitle>
      
      <DialogContent style={{ padding: 0 }}>
        <div className={classes.recordingContainer}>
          {/* Seção de Controles de Gravação */}
          <div className={classes.controlSection}>
            <Tooltip 
              title={
                isRecording 
                  ? (isPaused ? t('speech.resume') : t('speech.pause'))
                  : hasRecording 
                    ? t('speech.record_again') 
                    : t('speech.start_recording')
              }
            >
              <IconButton
                className={`${classes.recordingButton} ${
                  isRecording 
                    ? 'recording' 
                    : hasRecording 
                      ? 'has-recording' 
                      : ''
                }`}
                onClick={isRecording ? togglePauseRecording : startRecording}
                disabled={isProcessing}
              >
                {isRecording ? (
                  isPaused ? <Mic /> : <MicOff />
                ) : (
                  <Mic />
                )}
              </IconButton>
            </Tooltip>
            
            <Typography className={classes.timer}>
              {isRecording ? formatTime(recordingTime) : hasRecording ? formatTime(recordingDuration) : '00:00'}
            </Typography>
          </div>
          
          {/* Seção Central - Status/Loading ou Controles */}
          {isProcessing ? (
            <div className={classes.loadingSection}>
              <ThreeDots
                color={userDetails?.implementation_color || '#1976d2'}
                height={60}
                width={60}
                ariaLabel="loading"
              />
              <Typography 
                variant="body2" 
                className={classes.processingMessage}
                key={processingStep} // Key para forçar re-render da animação
                style={{ 
                  color: '#666', 
                  textAlign: 'center',
                  fontStyle: 'italic',
                  maxWidth: 350,
                  fontWeight: 500
                }}
              >
                {processingStep || t('speech.processing_audio')}
              </Typography>
            </div>
          ) : (
            <div className={classes.statusSection}>
              {/* Controles durante gravação */}
              {isRecording && (
                <div className={classes.actionButtons}>
                  <Tooltip title={t('speech.stop')}>
                    <Button
                      justIcon
                      color="secondary"
                      className={classes.cardButton}
                      onClick={stopRecording}
                    >
                      <Stop className={classes.cardButtonIcon} />
                    </Button>
                  </Tooltip>
                </div>
              )}
              
              {/* Controles de reprodução */}
              {hasRecording && !isRecording && (
                <div className={classes.actionButtons}>
                  <Tooltip title={isPlaying ? t('speech.pause') : t('speech.play')}>
                    <Button
                      justIcon
                      color="info"
                      className={classes.cardButton}
                      onClick={togglePlayback}
                      disabled={isProcessing}
                    >
                      {isPlaying ? <Pause className={classes.cardButtonIcon} /> : <PlayArrow className={classes.cardButtonIcon} />}
                    </Button>
                  </Tooltip>
                  
                  <Typography variant="body2" style={{ color: '#666', fontFamily: 'monospace' }}>
                    {formatTime(playbackTime)} / {formatTime(recordingDuration)}
                  </Typography>
                </div>
              )}
              
              {/* Status quando não há gravação */}
              {!hasRecording && !isRecording && (
                <Typography variant="body2" style={{ color: '#999', textAlign: 'center' }}>
                  {t('speech.ready_to_record')}
                </Typography>
              )}
            </div>
          )}
          
          {/* Seção de Ações */}
          <div className={classes.controlSection}>
            <Tooltip title={t('speech.process_audio')}>
              <Button
                justIcon
                color="client"
                className={classes.cardButton}
                onClick={processAudio}
                disabled={!hasRecording || isRecording || isProcessing}
              >
                <Send className={classes.cardButtonIcon} />
              </Button>
            </Tooltip>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

OnePage_SpeechRecorder.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSpeechProcessed: PropTypes.func.isRequired,
  selectedInitiative: PropTypes.object,
  t: PropTypes.func.isRequired
}

export default OnePage_SpeechRecorder
